import { ONBOARDING_ADD_METHOD } from '#~/analytics/constants/onboarding-events.js';
/* eslint-disable react/jsx-no-useless-fragment */
import { useSettings } from '#~/account/components/hooks.js';
import { Panel as SettingsPanel } from '#~/account/components/settings/types.js';
import { useAgents } from '#~/agent/hooks.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContextFromModel } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import {
  getBillingLimitsState,
  limitCanAddCamera,
  remainingCameraSlots
} from '#~/api/billing/limits.js';
import {
  applyManualRtspReset,
  clearManualRtspSeeds,
  seedRtspHelperFromDiscovery,
  shouldShowSelectionCapMessage
} from './utils.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import type { Minimist } from '#~/device/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
import { logger } from '#~/logger/logger.js';
import { useIsAdminOrOwner } from '#~/room/hooks.js';
import { Sel, STag } from '#~/stag/tag.js';
import { Anchor } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import { isCausedBy } from '@studio/js-utils/logger';
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { Protocol } from '@studio/models/control';
import { isRtsp } from '@studio/models/video/rtsp';
import { useLatestFn } from '@studio/react-utils/hooks';
import plur from 'plur';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import type { BulkCommitResult } from '../context.js';
import { resolveControlSettings } from '../control/normalize.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Panel } from '../store/index.js';
import { useWiring } from '../wiring.js';
import { resolveOnvifStreamVerdict } from './auth-verdict.js';
import { useAddSourceAuth } from './hooks.js';

/**
 * Shared "plan limit" callout used by both single-add (`LimitExceeded`) and
 * multi-add (`SelectionCapReached`). Extracted so both messages share the
 * exact `severity='info'` styling AND the admin/owner "Upgrade now" →
 * Billing-settings behavior (a single owner of that affordance), while each
 * caller supplies its own wording. Per `think-in-systems` (ai.knowledge/global).
 *
 * @param message - The explanatory copy (the message body / title).
 * @param className - Optional positioning override, forwarded to the Callout.
 */
const PlanLimitCallout = ({
  message,
  className
}: {
  message: string;
  className?: string | undefined;
}): ReactElement => {
  const settings = useSettings();
  const isAdminOrOwner = useIsAdminOrOwner();

  return (
    <Callout
      onClick={isAdminOrOwner ? () => settings(SettingsPanel.Billing) : undefined}
      className={twMerge(className, isAdminOrOwner && 'cursor-pointer')}
      severity='info'
      title={
        <div className='text-left text-sm'>
          {message}
          {isAdminOrOwner && <span className='px-4 font-medium text-primary'>Upgrade now</span>}
        </div>
      }
    />
  );
};

/**
 * Shared placement for the footer plan-limit callouts. `_Footer.Root` is a
 * `flex … justify-between` row whose three children are: a spacer `<div/>`, the
 * callout, and the primary button. The callout is the middle cell, so it sits
 * between the spacer (left) and the button (right).
 *
 * The callout must NOT overflow into the button's space. We constrain it with a
 * `max-w` (so the longer `SelectionCapReached` copy wraps to two lines instead
 * of running under the button) and reserve a right margin as clearance from the
 * button. This replaces the earlier `right-[-120px]` hack — which shifted the
 * callout rightward and made the (longer) selection-cap message overlap the
 * button. Shared by all three callouts so single-add and multi-add stay in the
 * same slot with the same clearance. Per `think-in-systems` (ai.knowledge/global).
 */
const PLAN_CALLOUT_PLACEMENT = 'max-w-[560px] mr-24';

/** LimitExceeded — single-add: the plan's source limit is already reached. */
export const LimitExceeded = ({ className }: { className?: string }): ReactElement => (
  <PlanLimitCallout
    className={className}
    message="You have reached your plan's limit for sources."
  />
);

/**
 * Copy for `SelectionCapReached`. `remaining` is the room left on the plan
 * (`remainingCameraSlots`: plan limit minus sources in use, e.g. 1 on a Pro
 * plan of 8 with 7 in use), not the plan's limit, so it is stated as sources
 * left. Returns `undefined` below 1: `LimitExceeded` owns the at-limit case.
 * Per rule: absent-data-gates-permissively (ai.knowledge/global) — unloaded limits read 0 too.
 */
export const resolveSelectionCapMessage = (remaining: number): string | undefined => {
  if (remaining < 1) return undefined;
  return `Your plan has room for ${remaining} more ${plur('source', remaining)}, and your selection fills it. To choose a different source, deselect one first.`;
};

/**
 * SelectionCapReached — multi-add: the current SELECTION has filled the room
 * left on the plan, so the remaining (unchecked) row checkboxes are
 * disabled. Explains WHY the user cannot keep ticking, mirroring
 * `LimitExceeded`'s style + admin/owner upgrade affordance (both share
 * `PlanLimitCallout`). Distinct from `LimitExceeded`: this is shown while the
 * user still has headroom under the plan (`remaining > 0`) but their
 * in-progress selection has consumed it — the gating condition lives in
 * `shouldShowSelectionCapMessage` (source/utils.ts). Per `think-in-systems`.
 *
 * @param remaining - How many more sources the plan has room for
 *   (`remainingCameraSlots`), not the plan's limit. Renders nothing below 1.
 */
export const SelectionCapReached = ({
  remaining,
  className
}: {
  remaining: number;
  className?: string;
}): ReactElement | null => {
  const message = resolveSelectionCapMessage(remaining);
  return message === undefined ? null : (
    <PlanLimitCallout className={className} message={message} />
  );
};

/**
 * Shown while billing limits are still loading (`unknown`) or the billing read
 * errored. Per ENG-4165/ENG-4187 — we must NOT claim "limit reached" before the
 * real limit is known; this neutral "checking your plan" state replaces the
 * LimitExceeded callout until limits resolve.
 */
export const CheckingPlan = ({
  className,
  status
}: {
  className?: string;
  status: 'unknown' | 'error';
}): ReactElement => {
  return (
    <Callout
      // Support-distinguishable signal: session-replay/support can read
      // data-billing-limits-state to tell "checking" apart from a real limit.
      // Per ENG-4165/ENG-4187 — kept minimal, no new analytics pipeline.
      data-billing-limits-state={status}
      className={className}
      severity='info'
      title={<div className='text-left text-sm'>Checking your plan&hellip;</div>}
    />
  );
};

/**
 * Minimum visible-dwell window for the "Adding…" CTA state, in
 * milliseconds. Per Design peer review, the user must see the spinner
 * long enough to register an action even if the bulk-add resolves
 * faster than the eye can track. This is the floor — the actual close
 * timing is `MAX(allSettled, 300ms)` (Promise.all waits for the slower
 * leg). `wiring.handleBulkCommit` owns the dwell at runtime; this
 * constant is exported for tests + parity assertions and mirrors
 * `BULK_CLOSE_DWELL_MS` in `../wiring.ts`.
 */
export const ADDING_DWELL_MS = 300;

/**
 * Pure label resolver for the bulk-mode CTA.
 *
 *   - Committing → "Adding…"
 *   - Idle, N=1 → "Add 1 Source"
 *   - Idle, N>=2 → "Add N Sources"
 *
 * Per `tests-require-user-story-comment` rule: extracting this as a pure
 * function lets the morph + pluralization contract be tested without
 * spinning up the full footer component graph.
 */
export const resolveBulkButtonLabel = (count: number, isCommitting: boolean): string => {
  if (isCommitting) return 'Adding…';
  return `Add ${count} ${plur('Source', count)}`;
};

/**
 * Pure preview-gate predicate for a video selection (Change 2B).
 *
 * Continue (and the matching "Waiting for preview…" label / disabled state) is
 * gated on the RTSP preview being connected — EXCEPT for two selections that
 * attempt no preview connection at all: Iris-enabled cameras (a different auth
 * flow) and brand-only fingerprint matches (a `brandId` with NO `modelId`,
 * whose `video.path` is empty so a preview can never connect). Both bypass the
 * gate. Returns `true` when the gate is still PENDING (preview required but not
 * yet connected) — i.e. Continue must stay blocked.
 *
 * Extracted pure so the gate-bypass contract (the three call sites: handleNext,
 * the label resolver, and the disabled predicate) is testable without spinning
 * up the full Footer component graph and its mock scaffold. (An earlier note
 * here claimed the component-level `.tsx` suite was un-runnable under ESM jest
 * due to lodash-es — that was footer.test.tsx's own partial lodash-es mock,
 * since removed; the component suite runs. See knowledge:
 * esm-jest-mock-patterns (ai.knowledge/global).)
 *
 * Per `tests-require-user-story-comment` + `think-in-systems` (ai.knowledge/global).
 */
export const isVideoPreviewPending = (selection: {
  irisEnabled?: boolean | undefined;
  isBrandOnlyMatch?: boolean | undefined;
  isPreviewConnected?: boolean | undefined;
  // ENG-4354 (multi-add): a known-model credential validation is in flight. The
  // preview device may already exist (isPreviewConnected true) yet Continue must
  // stay gated until the verdict lands. Optional so the handleNext call site can
  // omit it (navigation is not cred-gated there — the disabled button already
  // blocks the click during validation); the label + disabled predicate pass it.
  isValidatingCredentials?: boolean | undefined;
  isVideo?: boolean | undefined;
}): boolean =>
  !!selection.isVideo &&
  !selection.irisEnabled &&
  !selection.isBrandOnlyMatch &&
  (!selection.isPreviewConnected || !!selection.isValidatingCredentials);

/**
 * Inputs to the RTSP (ONVIF) Continue gate. An ONVIF discovery is control-only:
 * its video configuration exists only once discovery.tsx turns the probed
 * stream URI into `candidate.video`. `isVideoPreviewPending` never sees these
 * rows (`isVideo` is false), so before this gate Continue was enabled at once.
 */
export interface OnvifStreamGate {
  /** The selection is an RTSP (ONVIF) discovery (`cameraTransport === 'onvif'`). */
  isOnvif?: boolean | undefined;
  /** `candidate.video` is set (discovery.tsx built it from the stream URI). */
  hasCandidateVideo?: boolean | undefined;
  /** The ONVIF probe settled with NO usable stream URI: capabilities without one,
   *  a non-credential probe error, or the stall timeout (`resolveOnvifStreamVerdict`). */
  streamMissing?: boolean | undefined;
}

/**
 * True ⇢ Continue must stay blocked for an RTSP (ONVIF) selection: the probe
 * has not yet produced a video configuration and has not finished without one.
 * Advancing earlier would leave Configure video nothing to edit.
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const isOnvifStreamPending = ({
  isOnvif,
  hasCandidateVideo,
  streamMissing
}: OnvifStreamGate): boolean => !!isOnvif && !hasCandidateVideo && !streamMissing;

/**
 * True ⇢ Continue on an RTSP (ONVIF) selection whose probe settled without a
 * usable stream URI goes to the RTSP helper, seeded with the camera's host — the
 * same route a brand-only fingerprint match takes (panel.tsx / handleNext).
 * Chosen over leaving Continue disabled because the camera answered discovery;
 * only the stream address is unknown, and the RTSP helper is the screen that
 * collects exactly that (model template or URL).
 */
export const shouldRouteOnvifToRtspHelper = ({
  isOnvif,
  hasCandidateVideo,
  streamMissing
}: OnvifStreamGate): boolean => !!isOnvif && !hasCandidateVideo && !!streamMissing;

/** Inputs to `hasResolvedVideoAspect`. */
export interface VideoAspectInput extends OnvifStreamGate {
  /** The discovery row itself carries a video aspect (`'video' in selection`). */
  isVideoSelection?: boolean | undefined;
  /** `candidate.video` is not a bare RTSP stub with an empty path (footer selector). */
  candidateResolved?: boolean | undefined;
}

/**
 * True ⇢ the selection has a video aspect the footer should treat like any other
 * video discovery: gate Continue on its preview, and allow the known-model
 * one-click commit once that preview connects.
 *
 * A video discovery (RTSP/NDI/...) always qualifies, exactly as before. An RTSP
 * (ONVIF) discovery is control-only, so it qualifies once discovery.tsx turned the
 * probed stream URI into a connectable `candidate.video`. Without this, ONVIF rows
 * with a known model (Angekis Saber X, BirdDog X1, Bolin B220, ...) auto-filled
 * every field yet never one-click added, while the same cameras did over plain
 * RTSP.
 *
 * The ONVIF arm yields to the no-stream routing of `shouldRouteOnvifToRtspHelper`:
 * a 'missing' verdict, or the host-less RTSP marker that routing leaves on
 * `candidate.video` (not `candidateResolved`), never counts, because no preview can
 * connect for it and Continue would wait forever.
 *
 * Not plain `!!candidate.video`: a known-model RTSP row has no `candidate.video`
 * until its HTTP credential probe passes, and Continue must keep waiting there.
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
export const hasResolvedVideoAspect = ({
  isVideoSelection,
  isOnvif,
  hasCandidateVideo,
  streamMissing,
  candidateResolved
}: VideoAspectInput): boolean =>
  !!isVideoSelection || (!!isOnvif && !!hasCandidateVideo && !!candidateResolved && !streamMissing);

/**
 * True ⇢ Continue commits the camera directly (the known-model one-click
 * shortcut) instead of advancing into the Video/Manufacturer/Control panels.
 * Every input must hold: a resolved video aspect (`hasResolvedVideoAspect`), a
 * fingerprint-resolved model, a connected video preview, and a candidate that is
 * not a half-built RTSP stub. Extracted pure so the gate is testable without the
 * component scaffold. Per rule: think-in-systems (ai.knowledge/global).
 *
 * `previewFailed` vetoes the commit when the video preview already reported a
 * failure (the preview add was rejected, or the stream's driver rejected the
 * credentials). Continue then falls through to the manual advance, where the
 * failing stream is visible and editable. The footer only sets it for RTSP
 * (ONVIF) rows, so plain RTSP/NDI keep their existing shortcut. It never adds a
 * wait: with no failure reported, the gate is the same as before.
 */
export const isOneClickCommitReady = ({
  hasVideoAspect,
  hasModelId,
  isPreviewConnected,
  candidateResolved,
  previewFailed = false
}: {
  hasVideoAspect: boolean;
  hasModelId: boolean;
  isPreviewConnected: boolean;
  candidateResolved: boolean;
  previewFailed?: boolean;
}): boolean =>
  hasVideoAspect && hasModelId && isPreviewConnected && candidateResolved && !previewFailed;

/**
 * Build a dwell promise. Originally this raced a 300ms timer against
 * the commit handler from inside the footer; the dwell now lives in
 * `wiring.handleBulkCommit` itself (single source of truth for the
 * MAX(allSettled, 300ms) close-timing contract), so the footer no
 * longer composes its own dwell here.
 *
 * Kept exported because the helper is referenced by `footer.test.ts` and
 * mirrors the same shape as `wiring.ts`'s internal dwell — useful as a
 * test fixture and as a zero-dep timer primitive if a future caller
 * needs one.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — pure timer fn,
 * trivially testable.
 */
export const buildDwellPromise = (ms: number = ADDING_DWELL_MS): Promise<void> =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

/** Source `<Footer>` component. */
export const Footer = (props: _Footer.Props): ReactElement => {
  const producer = useProducer();
  const sourceAuth = useAddSourceAuth();
  const wiring = useWiring();
  const [isNavigating, setIsNavigating] = useState(false);
  // `isCommitting` drives the bulk-mode "Adding…" dwell.
  // Distinct from `isNavigating` (single-add path) so the two flows
  // never share state by accident. Per `think-in-systems`
  // (ai.knowledge/global).
  const [isCommitting, setIsCommitting] = useState(false);

  const { track } = useAnalytics();
  const roomId = useStore((µ) => µ.roomId);
  // Per ENG-3960 §E12 — read canonical device cohort props from manufacturer config.
  const manufacturerConfig = useStore((µ) => µ.manufacturer.config);

  // First-run funnel control surface, and the exit it takes over with. Undefined
  // for every non-onboarding caller, so the known-model close branch below is
  // dead outside onboarding (mirrors manufacturer/footer.tsx, the configured
  // path's own onConfigured site). See OnboardingHandoff/OnboardingExit.
  const onboarding = useStore((µ) => µ.onboarding);
  const onboardingExit = useStore(
    (µ) => ({
      candidate: µ.candidate,
      agentId: µ.agentId,
      selection: µ.source.selection,
      template: µ.manufacturer.config,
      defaultName: µ.manufacturer.defaultName
    }),
    shallow
  );

  const {
    selection,
    selectionModelId,
    isBrandOnlyMatch,
    requiresAuth,
    isPreviewConnected,
    isValidatingCredentials,
    bulkMode,
    multiSelectionCount,
    candidateResolved,
    isOnvif,
    hasCandidateVideo,
    onvifStreamMissing,
    videoPreviewFailed
  } = useStore(({ source, candidate, previews, authSkipped, external }) => {
    let requiresAuth = false;
    // True when the control preview errored for a reason OTHER than rejected
    // credentials — a settled RTSP (ONVIF) probe that will never report a stream.
    let controlProbeFailed = false;
    // Protocols whose control preview carries an auth verdict: ONVIF (SOAP rejects
    // unauthenticated calls 400/401/403) and HTTP (fingerprint-matched discoveries —
    // panel.tsx sets candidate.control to HTTP so the probe runs the model's Login and a
    // rejection surfaces through the same previews.control.error path).
    const credentialProtocols: Protocol[] = [Protocol.ONVIF, Protocol.HTTP];
    if (
      source.selection &&
      previews.control &&
      candidate.control?.protocol &&
      credentialProtocols.includes(candidate.control.protocol as Protocol)
    ) {
      const { error } = previews.control;

      // Do we have an error?
      if (error) {
        // Whoops.  We have an error.  What went wrong?
        //
        // ENG-4220 audit note: these substrings match the encoder SOAP
        // layer's "unexpected response status code: %d: %s" format
        // (bridge/encoder/pkg/protocol/soap/conn.go + http.StatusText), so
        // unlike the dead discovery timeout guard they are live. Keep in
        // sync with discovery.tsx's identical guard.
        requiresAuth =
          isCausedBy(error, '400: Bad Request') ||
          isCausedBy(error, '401: Unauthorized') ||
          isCausedBy(error, '403: Forbidden');
        controlProbeFailed = !requiresAuth;
      }
    }

    // The rejection itself, before the operator Skip below, as discovery.tsx's RTSP
    // (ONVIF) error branch reads it.
    const controlCredentialsRejected = requiresAuth;

    // Skip-credentials Gate B (impl-spec §4/§5): honor the persistent operator Skip.
    // Thread the store-level authSkipped flag to enable Continue rather than relying on
    // candidate.control HTTP-substitution swapping off (the spec warns that's fragile).
    requiresAuth = requiresAuth && !authSkipped;

    // A late model template clears video for its HTTP probe before the old preview is removed.
    const isPreviewConnected = !!candidate.video && !!previews.video?.deviceId;

    // In-flight credential validation: either preview tmp reports per-aspect Authentication
    // VALIDATING (mirrors discovery.tsx's videoAuthValidating). Continue must stay disabled
    // through this window even when isPreviewConnected is already true. Honors operator Skip
    // (authSkipped) like requiresAuth, so a skipped probe re-enables Continue immediately.
    const videoDeviceId = previews.video?.deviceId;
    const controlDeviceId = previews.control?.deviceId;
    const isValidatingCredentials =
      !authSkipped &&
      ((!!videoDeviceId &&
        external?.transients?.[videoDeviceId]?.videoAuthenticationStatus ===
          AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATING) ||
        (!!controlDeviceId &&
          external?.transients?.[controlDeviceId]?.controlAuthenticationStatus ===
            AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATING));

    // The video preview reported a failure: the mediator recorded a rejected
    // preview add (previews.video.error, which still carries a deviceId, so
    // isPreviewConnected alone reads true), or the stream's driver rejected the
    // credentials (INVALID, unless the operator skipped). Vetoes the RTSP (ONVIF)
    // one-click commit only; see isOneClickCommitReady.
    const videoPreviewFailed =
      !!previews.video?.error ||
      (!authSkipped &&
        !!videoDeviceId &&
        external?.transients?.[videoDeviceId]?.videoAuthenticationStatus ===
          AuthenticationStatus.AUTHENTICATION_STATUS_INVALID);

    // The known-model one-click shortcut may only fire on a fully-resolved
    // config. When the template fails to substitute (registry error, load race,
    // or the extended-discovery flag off), candidate.video is a bare RTSP stub
    // with an empty path (rtsp://host:554/). Treat that as unresolved so control
    // falls through to the safe manual advance; NDI and complete RTSP resolve.
    const candidateVideo = candidate.video;
    const candidateResolved =
      !!candidateVideo &&
      !(isRtsp(candidateVideo.protocol) && !(candidateVideo as { path?: string }).path);

    // RTSP (ONVIF) gate inputs (see isOnvifStreamPending). The verdict reads what
    // discovery.tsx reads, in the same order (preview error, published capabilities,
    // stall), so the badge and the footer agree.
    // A discovery carries exactly one aspect, so an ONVIF control aspect means the
    // row is control-only — the 'onvif' transport of discovery-transports.ts.
    const selectionControl =
      source.selection && 'control' in source.selection ? source.selection.control : undefined;
    const isOnvif = selectionControl?.protocol === Protocol.ONVIF;
    // Settled = a non-credential probe error, capabilities without a usable URI, or the
    // stall budget (discovery.tsx) having run out for the current probe attempt. The
    // budget also covers a control preview add the bridge has not answered, so no
    // preview id is required here; requiring one would hold Continue on "Waiting for
    // preview..." until the add request times out.
    const onvifStreamMissing =
      isOnvif &&
      candidate.control?.protocol === Protocol.ONVIF &&
      resolveOnvifStreamVerdict(
        controlDeviceId
          ? external?.transients?.[controlDeviceId]?.control?.capabilities
          : undefined,
        {
          credentialsRejected: controlCredentialsRejected,
          probeFailed: controlProbeFailed,
          stalled: !!source.onvifProbe?.stalled
        }
      ) === 'missing';

    return {
      selection: source.selection,
      // Fingerprint-resolved modelId from the discovery selection. When
      // present, handleNext commits directly from here instead of
      // advancing into the Video/Manufacturer/Control config panels —
      // the template already carries everything those panels would
      // collect, so there's nothing left to configure.
      selectionModelId: source.selection?.modelId,
      // Brand-level fingerprint match (Change 2B): a brandId with NO
      // modelId. Like Iris-enabled, this row attempts no preview
      // connection (its video.path is empty), so Continue must bypass the
      // isPreviewConnected gate. handleNext routes it to the RTSP Helper.
      isBrandOnlyMatch: !!(source.selection?.brandId && !source.selection?.modelId),
      requiresAuth,
      isPreviewConnected,
      isValidatingCredentials,
      // bulk-mode signal drives CTA pluralization, dwell behavior,
      // and disabled-when-N=0 logic.
      bulkMode: source.bulkMode,
      multiSelectionCount: source.multiSelection.length,
      candidateResolved,
      // Flat booleans (not one object) so the `shallow` comparator stays meaningful.
      isOnvif,
      hasCandidateVideo: !!candidate.video,
      onvifStreamMissing,
      videoPreviewFailed
    };
  }, shallow);

  // The selected row's registry template, which source/panel.tsx and discovery.tsx
  // already load. The RTSP (ONVIF) one-click add resolves its control against it,
  // from the candidate's probed control and stream host.
  const template = useTemplate(selectionModelId);
  const candidate = useStore((µ) => µ.candidate);

  // RTSP (ONVIF) gate, shared by handleNext, the label and the disabled predicate.
  const onvifGate: OnvifStreamGate = {
    isOnvif,
    hasCandidateVideo,
    streamMissing: onvifStreamMissing
  };

  // One video-aspect predicate for the preview gate (handleNext, label, disabled)
  // and the one-click commit, so an RTSP (ONVIF) row whose probe built
  // candidate.video waits for that preview and can then one-click add.
  const hasVideoAspect = hasResolvedVideoAspect({
    ...onvifGate,
    isVideoSelection: !!selection && 'video' in selection,
    candidateResolved
  });

  const handleManualRtsp = useLatestFn(() => {
    if (!limitCanAddCamera()) {
      // We shouldn't be getting in here, however, just in case lets also have this check.
      // This is due to the DOM element being disabled, so, the user can't click the 'add manual RTSP source'.
      return;
    }

    track(EVENT_NAMES.DEVICE_CLICK_MANUAL_ADD, EVENT_CATEGORIES.DEVICE, {
      studio_id: roomId
    });

    producer((draft) => {
      draft.addMethod = ONBOARDING_ADD_METHOD.MANUAL;
      // B4 (ENG-4159): a fresh manual RTSP add must start with brand + model
      // BLANK. applyManualRtspReset clears candidate.video's host (as before)
      // AND candidate.modelId — the load-bearing field rtsp.tsx seeds the Model
      // dropdown from and derives the Brand from — while deliberately leaving
      // source.seededBrandId (the intentional Change-2B brand-only seed)
      // untouched. Per `reference-rules-in-comments` (ai.knowledge/global).
      applyManualRtspReset(draft.candidate);

      // B12: also clear the brand-only fingerprint seeds. applyManualRtspReset
      // scopes itself to `candidate`, but rtsp.tsx also pre-seeds its Brand
      // dropdown + IP field from `source.seededBrandId` / `source.seededHost`.
      // Without clearing those, choosing "Manually Configure an RTSP source"
      // after selecting a brand-only discovery row opened the Helper with that
      // brand + IP pre-filled instead of blank. The BLANK contract wins.
      clearManualRtspSeeds(draft.source);

      draft.panel = Panel.Video;
    });
  });

  const handleNext = useLatestFn(async () => {
    // Prevent multiple simultaneous navigation attempts
    if (isNavigating || !selection) return;

    // For video sources, ensure preview is connected before allowing navigation
    // EXCEPT for iris-enabled cameras (different auth flow) and brand-only
    // fingerprint matches (Change 2B) — both attempt no preview connection.
    // (See `isVideoPreviewPending` for the gate-bypass contract.)
    if (
      isVideoPreviewPending({
        irisEnabled: selection.irisEnabled,
        isBrandOnlyMatch,
        isPreviewConnected,
        // Includes an RTSP (ONVIF) row once its stream URI built candidate.video.
        isVideo: hasVideoAspect
      })
    ) {
      console.warn('___Cannot navigate: Preview not connected yet');
      return;
    }

    producer((draft) => {
      draft.addMethod = ONBOARDING_ADD_METHOD.DISCOVERY;
    });
    // RTSP (ONVIF): never advance before the probe resolved a stream (or finished
    // without one); with no candidate.video, Configure video has nothing to edit.
    // The disabled button already blocks the click; this guards races.
    if (isOnvifStreamPending(onvifGate)) {
      logger.warning('add-source: ONVIF stream URI not resolved yet; Continue blocked');
      return;
    }

    setIsNavigating(true);

    try {
      if (selection?.irisEnabled) {
        sourceAuth();
        return;
      }

      // Brand-level fingerprint match (Change 2B): the encoder resolved a
      // brand but no model, so there is nothing to preview-connect and no
      // template to commit from. Route straight to the RTSP Helper
      // (Panel.Video) — mirroring `handleManualRtsp`'s jump above — where the
      // brand is pre-seeded (rtsp.tsx reads source.seededBrandId, set by
      // panel.tsx) and the user picks the model. This bypasses the
      // isPreviewConnected gate exactly like the Iris-enabled exception.
      if (isBrandOnlyMatch) {
        track(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, EVENT_CATEGORIES.DEVICE, {
          deviceId: selection.id,
          sourceType: 'video' in selection ? selection.video.protocol : 'control',
          studio_id: roomId
        });
        producer((draft) => {
          draft.panel = Panel.Video;
        });
        return;
      }

      // RTSP (ONVIF) whose probe settled without a usable stream URI: route to the
      // RTSP helper like a brand-only match, with the camera's host pre-filled
      // (source.seededHost) and a host-less RTSP marker so content.tsx renders the
      // helper without spinning up a doomed preview. The ONVIF credentials stay on
      // candidate.control, which the helper's credential fields fall back to (rtsp.tsx
      // controlCreds).
      if (shouldRouteOnvifToRtspHelper(onvifGate)) {
        track(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, EVENT_CATEGORIES.DEVICE, {
          deviceId: selection.id,
          sourceType: 'control',
          studio_id: roomId
        });
        producer((draft) => {
          seedRtspHelperFromDiscovery(draft, selection);
          draft.panel = Panel.Video;
        });
        return;
      }

      // Known-model shortcut: fingerprint-resolved discoveries skip the
      // Video/Manufacturer/Control config panels entirely and commit from
      // here. By the time the user gets to click Continue, four things
      // are already true:
      //
      //   * selection.modelId resolved the device in the Device Registry
      //     template
      //   * `candidate` holds a resolved video config: for a video
      //     known-model row, panel.tsx + discovery.tsx swapped in the
      //     template's real video/control configs (URL paths, ports, driver
      //     protocols, auth creds); for an RTSP (ONVIF) row, discovery.tsx
      //     built candidate.video from the camera's own probed stream URI
      //   * the credential probe passed (otherwise the creds modal would
      //     still be up and we wouldn't reach handleNext): the HTTP probe for
      //     a video known-model row, the ONVIF SOAP probe for an RTSP (ONVIF)
      //     row (candidate.control stays ONVIF there; no HTTP probe runs)
      //   * isPreviewConnected = true: the video preview device was created
      //     on the bridge (previews.video.deviceId). This does not prove the
      //     stream plays; the mediator also sets the id when the add fails.
      //     For RTSP (ONVIF) rows a reported preview failure vetoes the
      //     shortcut (previewFailed below)
      //
      // There's nothing left for the config panels to collect. Commit
      // directly. Legacy (no modelId) selections still fall through to
      // the panel advance below.
      //
      // RTSP (ONVIF) rows qualify too: the row is control-only, but its probed
      // stream URI became candidate.video (discovery.tsx) and that preview is
      // what isPreviewConnected reads.
      if (
        selection &&
        isOneClickCommitReady({
          hasVideoAspect,
          hasModelId: !!selectionModelId,
          isPreviewConnected,
          // Only commit directly when the candidate is actually resolved, not a
          // half-built RTSP stub (see candidateResolved above).
          candidateResolved,
          // RTSP (ONVIF) only: a preview that already reported a failure goes to
          // the manual Video panel instead of committing a broken stream. Plain
          // RTSP/NDI rows keep their existing shortcut (stay-in-scope).
          previewFailed: isOnvif && videoPreviewFailed
        })
      ) {
        // One-Click shortcut predicate (proposal §5a-1): stamp add_mode='one_click' so the
        // dashboard splits One-Click from manual explicitly, not by absence-inference, and
        // restore the cohort context the sibling manual site sends. manufacturer.config.model
        // is set only by the manual picker this path bypasses, so pass selectionModelId as the
        // fallback model id (like the wiring.ts/discovery.tsx siblings) — else the resolver
        // returns {} and cohort props never attach.
        const deviceAnalyticsCatalogContext = resolveDeviceAnalyticsCatalogContextFromModel(
          manufacturerConfig?.model,
          selectionModelId
        );
        track(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, EVENT_CATEGORIES.DEVICE, {
          deviceId: selection.id,
          sourceType: 'video' in selection ? selection.video.protocol : 'control',
          add_mode: 'one_click',
          studio_id: roomId,
          ...deviceAnalyticsCatalogContext
        });

        // RTSP (ONVIF): candidate.control is still the ONVIF endpoint the probe used,
        // and the app does not drive ONVIF for PTZ. Commit the control the model
        // template resolves to (normalize.ts), as Connect controls, multi-add and the
        // first-run funnel do. It goes to handleCommit, not into the store: the preview
        // Mediator would answer a store write with a preview for the new control, and
        // Discovery would drop candidate.video, while the commit runs. Per rule:
        // absent-data-gates-permissively (ai.knowledge/global), a template that has not
        // loaded does not block the add; the probed control stays.
        const committedDeviceId = await wiring.handleCommit(
          isOnvif
            ? {
                control: resolveControlSettings(
                  candidate.control,
                  template,
                  candidate.video
                ) as Minimist['control']
              }
            : undefined
        );

        // Bug 3 (known-model onboarding close): in the first-run funnel the
        // configured add path fires onboarding.onConfigured from the model step
        // (manufacturer/footer.tsx) so the funnel takes over and runs its own
        // completion journey. This known-model 1-click path skips the model step,
        // so committing the camera without firing that same signal left the
        // wizard stuck on the discovery screen. Fire it here with the committed
        // device id so the known-model path closes the wizard exactly like the
        // configured path. handleCommit keeps the workflow mounted (returns the
        // id without closing) whenever onboarding is set, so this is the only
        // close signal. Undefined for every other caller, so the normal Add flow
        // is untouched.
        if (onboarding !== undefined) {
          onboarding.onConfigured({ ...onboardingExit, deviceId: committedDeviceId as never });
        }
        return;
      }

      if (selection) {
        const deviceAnalyticsCatalogContext = resolveDeviceAnalyticsCatalogContextFromModel(
          manufacturerConfig?.model
        );

        // Per ENG-3960 §E12 — include canonical device cohort props from manufacturer config.
        // Per ENG-3960 §1.7 — additively carry is_embedded + linking_surface when embedded
        // (iris-enabled selections short-circuit to sourceAuth above; this covers the rest).
        track(EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO, EVENT_CATEGORIES.DEVICE, {
          deviceId: selection.id,
          sourceType: 'video' in selection ? selection.video.protocol : 'control',
          // Additive (proposal §5a-1): the manual advance is the sibling of the
          // One-Click site above. Both share one wire name, so stamping
          // add_mode='manual' here makes the One-Click vs manual split explicit
          // instead of resting the manual denominator on absence-inference.
          add_mode: 'manual',
          studio_id: roomId,
          ...deviceAnalyticsCatalogContext,
          ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(selection))
        });
      }

      // Simply navigate - the preview is already connected
      producer((draft) => {
        if (selection) {
          // Ensure candidate configuration is set from selection if needed
          if ('video' in selection && selection.video && !draft.candidate.video) {
            draft.candidate.video = selection.video;
          }
          draft.panel++;
        }
      });
    } catch (error) {
      console.error('Navigation error:', error);
    } finally {
      setIsNavigating(false);
    }
  });

  /**
   * Bulk-commit click handler.
   *
   * Drives the bulk-add commit pipeline via `wiring.handleBulkCommit`.
   * The dwell + close-timing contract (`MAX(allSettled, 300ms)`) lives
   * inside `wiring.handleBulkCommit` itself so there is one canonical
   * owner of the timing rule. The footer's only timing responsibility
   * is keeping `isCommitting` true while the wiring promise is pending
   * so the CTA renders the "Adding…" morph + `aria-busy="true"` for
   * the entire dwell window.
   *
   * Per rule: think-in-systems (ai.knowledge/global) — single-writer
   * principle for the dwell contract; the footer is a passive observer
   * of the wiring promise rather than a co-owner of timing.
   */
  const handleBulkClick = useLatestFn(async () => {
    // Re-entry guard — same pattern as handleNext.
    if (isCommitting || multiSelectionCount === 0) return;

    setIsCommitting(true);

    try {
      // Delegate to the wiring's bulk-commit pipeline. The cast widens
      // the return-type erasure (we don't consume `BulkCommitResult` at
      // this site — wiring drives modal close + focus return on its own
      // clock, see `wiring.scheduleBulkClose`). Optional-chain because
      // `handleBulkCommit` is `T_Maybe<...>` per `context.ts`.
      // Per `reference-rules-in-comments` (ai.knowledge/global) — see
      // `BulkCommitResult` definition in `../context.ts`.
      const commit = (
        wiring as { handleBulkCommit?: () => Promise<BulkCommitResult> }
      ).handleBulkCommit?.();

      // Await whatever the wiring returns. wiring.handleBulkCommit
      // already enforces the 300ms dwell floor before resolving; the
      // footer just keeps the morph/aria state pinned for the duration.
      await commit;
    } finally {
      // Restore idle state — modal close is owned by wiring's commit pipeline.
      setIsCommitting(false);
    }
  });

  // Non-embedded agents only: this drives the manual-RTSP affordance below
  // (embedded cameras cannot host a manual RTSP source). The SOURCE LIMIT
  // count is separate and intentionally counts ALL agents inside
  // limitCanAddCamera() per ENG-3023 (#945) — do not reconcile by excluding
  // embedded from the count, that would re-open the bypass.
  const agents = useAgents((µ) => µ.filter((a) => !a.embedded), shallow);

  // Per ENG-4165/ENG-4187 — tri-state billing limits. While unknown/error we
  // show a neutral "checking your plan" state and gate permissively
  // (limitCanAddCamera returns true), never the "limit reached" callout.
  const limitsState = getBillingLimitsState();
  const limitCanAddCameraResult = limitCanAddCamera();

  // Multi-add: the plan's remaining-source cap + whether the in-progress
  // selection has filled it. Drives <SelectionCapReached> in the bulk footer —
  // rendered in the SAME slot as, and mutually exclusive with, <LimitExceeded>
  // (LimitExceeded owns the already-at-max case; this owns the "your selection
  // filled the remaining slots" case, cap>0). Predicate is pure + unit-tested
  // (source/utils.ts).
  const cap = remainingCameraSlots();
  const showSelectionCapMessage = shouldShowSelectionCapMessage({
    isBulkAddEnabled: bulkMode,
    cap,
    selectedCount: multiSelectionCount
  });

  // Support-distinguishable signal: one logger line when limits aren't yet
  // known, so support/session-replay can tell "checking" from a real limit.
  // Per ENG-4165/ENG-4187 — minimal; fires on status transitions only.
  useEffect(() => {
    if (limitsState.status !== 'loaded') {
      logger.info(
        `add-source: billing limits not resolved (status=${limitsState.status}); gating permissively`
      );
    }
  }, [limitsState.status]);

  // Button label based on preview connection state
  const getButtonLabel = () => {
    if (isNavigating) {
      return 'Loading...';
    }

    // For video sources, show different text when waiting for preview connection
    // EXCEPT for iris-enabled cameras and brand-only matches (Change 2B) which
    // don't need a preview before proceeding. Also show it while a known-model
    // credential validation is in flight (ENG-4354): the preview device may
    // already exist (isPreviewConnected true) but the credentials are still being
    // validated, so proceeding is not yet legitimate.
    if (
      selection &&
      isVideoPreviewPending({
        irisEnabled: selection.irisEnabled,
        isBrandOnlyMatch,
        isPreviewConnected,
        isValidatingCredentials,
        isVideo: hasVideoAspect
      })
    ) {
      return 'Waiting for preview...';
    }

    // RTSP (ONVIF): same label while the probe is still resolving the stream URI.
    if (isOnvifStreamPending(onvifGate)) {
      return 'Waiting for preview...';
    }

    return 'Continue';
  };

  // Bulk-mode label is resolved by the pure `resolveBulkButtonLabel`
  // helper (see top of file) so the morph contract is testable without
  // spinning up the full Footer component tree.

  // Bulk-mode rendering: a single primary button gated on multiSelection
  // size. Same `_Footer.Next` component to keep the visual contract with
  // the rest of the editor's footer (variant=primary, size=lg).
  if (bulkMode) {
    return (
      <_Footer.Root {...props}>
        <div />
        {/* Plan-limit messaging, same footer slot for both. LimitExceeded owns
            the already-at-max case (cap===0); SelectionCapReached owns the
            "your selection filled the remaining slots" case (cap>0). Mutually
            exclusive via the limitCanAddCamera gate — never both. */}
        {!limitCanAddCameraResult ? (
          <LimitExceeded className={PLAN_CALLOUT_PLACEMENT} />
        ) : (
          showSelectionCapMessage && (
            <SelectionCapReached remaining={cap} className={PLAN_CALLOUT_PLACEMENT} />
          )
        )}
        <_Footer.Next
          // Disabled when N=0 OR while committing (dwell window). The
          // CTA must be disabled when N=0.
          disabled={multiSelectionCount === 0 || isCommitting || !limitCanAddCameraResult}
          // Per Design peer review: aria-busy during the 300ms dwell
          // signals progress to assistive tech.
          aria-busy={isCommitting ? 'true' : undefined}
          label={resolveBulkButtonLabel(multiSelectionCount, isCommitting)}
          onClick={limitCanAddCameraResult ? handleBulkClick : undefined}
          {...STag(Sel.AddVideoDeviceDiscovery, 'footer.bulk-add')}
        />
      </_Footer.Root>
    );
  }

  return (
    <_Footer.Root className={selection ? 'sticky bottom-0 z-10' : undefined} {...props}>
      <div>
        {agents.length !== 0 ? (
          <>
            <Anchor
              onClick={handleManualRtsp}
              disabled={!limitCanAddCameraResult}
              {...STag(Sel.AddVideoDeviceDiscovery, 'manual-rtsp')}
            >
              Manually Configure
            </Anchor>{' '}
            <span
              className={twMerge(
                !limitCanAddCameraResult ? 'cursor-not-allowed leading-[14px] text-muted' : ''
              )}
            >
              an RTSP source.
            </span>
          </>
        ) : (
          <></>
        )}
      </div>
      {/* Per ENG-4165/ENG-4187 — while limits are unknown/error, show the
          neutral "checking your plan" state; show LimitExceeded ONLY when
          limits are loaded AND genuinely exceeded. */}
      {limitsState.status !== 'loaded' ? (
        <CheckingPlan status={limitsState.status} className={PLAN_CALLOUT_PLACEMENT} />
      ) : (
        !limitCanAddCameraResult && <LimitExceeded className={PLAN_CALLOUT_PLACEMENT} />
      )}
      <_Footer.Next
        disabled={
          !selection ||
          requiresAuth ||
          !limitCanAddCameraResult ||
          isNavigating ||
          // Scoped identically to the "Waiting for preview..." label above so the
          // disabled state and label never diverge: video (non iris-enabled, non
          // brand-only) selections gate on preview + in-flight cred validation.
          // The existing preview gate excludes irisEnabled and brand-only matches;
          // the isValidatingCredentials gate rides the same helper (per Devin
          // review on #1100).
          isVideoPreviewPending({
            irisEnabled: selection.irisEnabled,
            isBrandOnlyMatch,
            isPreviewConnected,
            isValidatingCredentials,
            isVideo: hasVideoAspect
          }) ||
          // RTSP (ONVIF): blocked until a stream URI produced candidate.video, or the
          // probe finished without one (then Continue routes to the RTSP helper).
          isOnvifStreamPending(onvifGate)
        }
        label={getButtonLabel()}
        onClick={limitCanAddCameraResult ? handleNext : undefined}
        {...STag(Sel.AddVideoDeviceDiscovery, 'footer.continue')}
      />
    </_Footer.Root>
  );
};
